import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { DATE_FORMAT, ERROR_SAVING_DATA } from 'src/app/app.constants';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { EventModalMode } from 'src/app/shared/enums/event-modal-mode';
import {
  EmployerEvent,
  EmployerEventOptions,
  EventAssigneeGroup,
  EventAssigneeOption,
} from 'src/app/shared/interfaces/employer-event';
import { FilterOption } from '../../employer-overview/employer-overview-interfaces';
import { SharedService } from 'src/app/core/services/shared.service';
import { FromValidators } from 'src/app/core/common-forms/form-validators';
import { EmployerService } from '../../employer.service';

// The order of the company groups, which follow the "Currently assigned" group when there is one (#1807).
enum AssigneeGroupRank {
  OwnCompany = 1,
  OtherCompany = 2,
  NoCompany = 3,
}

@Component({
  selector: 'app-employer-events-form',
  templateUrl: './employer-events-form.component.html',
  styleUrls: ['./employer-events-form.component.scss'],
})
export class EmployerEventsFormComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      mode: EventModalMode;
      event: EmployerEvent | null;
      eventOptions: EmployerEventOptions;
      staffId: number;
      rootId: number;
      branchId: number | null;
      permissions: { canEdit: boolean; canDelete: boolean };
      showBranch: boolean;
    },
    private readonly fb: UntypedFormBuilder,
    private readonly dialogRef: MatDialogRef<EmployerEventsFormComponent, { refresh: boolean }>,
    private readonly matDialog: MatDialog,
    public sharedService: SharedService,
    private readonly matSnackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private readonly formValidators: FromValidators,
    private readonly employerService: EmployerService,
    private readonly liveAnnouncer: LiveAnnouncer
  ) {}

  staffId: number;
  rootId: number;
  branchId: number | null;
  saveError = ERROR_SAVING_DATA;
  dateFormat = DATE_FORMAT;
  eventModalMode = EventModalMode;

  plannedEventIds = [1, 2, 3, 4, 5, 6, 7, 12, 13, 20, 26, 27];
  healthAndSafetyEventIds = [1, 2, 3];

  private _staffSearchFilter = '';
  private staffDebounceTimer: NodeJS.Timer;

  get staffSearchFilter(): string {
    return this._staffSearchFilter;
  }

  set staffSearchFilter(value: string) {
    this._staffSearchFilter = value;

    if (this.staffDebounceTimer) {
      clearTimeout(this.staffDebounceTimer);
    }

    this.staffDebounceTimer = setTimeout(() => {
      this.filterEventStaff();
      // Tell screen-reader users what the search left (WCAG 4.1.3). LiveAnnouncer uses one region outside the
      // dropdown overlay, so it is heard even though the list itself is rebuilt.
      this.liveAnnouncer.announce(this.describeAssignees());
    }, 300);
  }

  private _branchSearchFilter = '';
  private branchDebounceTimer: NodeJS.Timer;

  get branchSearchFilter(): string {
    return this._branchSearchFilter;
  }

  set branchSearchFilter(value: string) {
    this._branchSearchFilter = value;

    if (this.branchDebounceTimer) {
      clearTimeout(this.branchDebounceTimer);
    }

    this.branchDebounceTimer = setTimeout(() => {
      this.filterEventBranch();
    }, 300);
  }

  private _typeSearchFilter = '';
  private typeDebounceTimer: NodeJS.Timer;

  get typeSearchFilter(): string {
    return this._typeSearchFilter;
  }

  set typeSearchFilter(value: string) {
    this._typeSearchFilter = value;

    if (this.typeDebounceTimer) {
      clearTimeout(this.typeDebounceTimer);
    }

    this.typeDebounceTimer = setTimeout(() => {
      this.filterEventTypes();
    }, 300);
  }

  event: EmployerEvent = null;
  mode: EventModalMode = EventModalMode.view;
  eventsForm: UntypedFormGroup;
  saving = false;
  eventOptions: EmployerEventOptions;
  filteredEventTypes: FilterOption[] = [];
  // Everyone who can be picked as the assignee, and the same list grouped by company for display.
  assigneeOptions: EventAssigneeOption[] = [];
  filteredEventStaff: EventAssigneeOption[] = [];
  assigneeGroups: EventAssigneeGroup[] = [];
  filteredEventBranches: FilterOption[] = [];

  timeStart = 7;
  timeEnd = 19;
  timeIntervals = 15;
  startTimes = [];
  endTimes = [];

  permissions: {
    canEdit: boolean;
    canDelete: boolean;
  };

  refreshEvents = false;

  ngOnInit(): void {
    this.makeTimeIntervals();
    this.permissions = this.data.permissions;
    this.mode = this.data.mode;
    this.event = this.data.event;
    this.eventOptions = this.data.eventOptions;
    this.staffId = this.data.staffId;
    this.rootId = this.data.rootId;
    this.branchId = this.data.branchId;
    this.filteredEventTypes = [...this.eventOptions.types];
    this.refreshAssigneeOptions();
    this.filteredEventBranches = [...this.eventOptions.branches];
    this.buildForm();
    if (this.event?.startTime !== undefined) {
      this.setEndTimes(this.event.startTime, false);
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    // A search typed just before closing must not run (or be announced) after the dialog has gone.
    clearTimeout(this.staffDebounceTimer);
    clearTimeout(this.branchDebounceTimer);
    clearTimeout(this.typeDebounceTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  makeTimeIntervals(): void {
    for (let i = this.timeStart; i < this.timeEnd; i++) {
      for (let j = 0; j < 60; j += this.timeIntervals) {
        const time = `${i.toString().padStart(2, '0')}:${j.toString().padStart(2, '0')}`;
        this.startTimes.push(time);
      }
    }
    this.endTimes = [...this.startTimes, this.timeEnd + ':00'];
  }

  // Endt times should be greater than start selected
  setEndTimes(startTime: string, clearEnd = true): void {
    if (clearEnd) {
      this.eventsForm.controls.endTime.setValue(null);
    }
    this.endTimes = [...this.startTimes, this.timeEnd + ':00']
      .filter((x) => x > startTime)
      .map((x) => x + ' (' + this.timeDifference(startTime, x) + ')');
  }

  timeDifference(startTime: string, endTime: string): string {
    const start = startTime.split(':');
    const end = endTime.split(':');
    const hoursInMins = (parseInt(end[0]) - parseInt(start[0])) * 60;
    const minsDiffrance = parseInt(end[1]) - parseInt(start[1]);
    const totalMins = Math.abs(hoursInMins + minsDiffrance);
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours === 0) {
      return `${mins} mins`;
    }
    if (mins === 0) {
      return `${hours} hours`;
    }
    return `${hours} hours ${mins} mins`;
  }

  buildForm(): void {
    this.eventsForm = this.fb.group(
      {
        type: [this.filteredEventTypes.find((t) => t.id === this.event?.type.id), Validators.required],
        branch: [this.filteredEventBranches.find((b) => b.id === this.event?.branchId)],
        assignee: [
          this.event === null || this.event?.id === null
            ? this.assigneeOptions.find((s) => s.id === this.staffId)
            : this.assigneeOptions.find((s) => s.id === this.event?.assignee.id),
          Validators.required,
        ],
        notes: [this.event?.notes],
        plannedDate: [this.event?.plannedDate],
        actualDate: [this.event?.actualDate],
        startTime: [this.event?.startTime],
        endTime: [this.event?.endTime],
        outlookEvent: [this.event?.outlookEvent === undefined ? false : this.event?.outlookEvent.active],
        outlookSubject: [this.event?.outlookEventSubject],
      },
      {
        validators: [
          this.formValidators.atLeastOneValidator(['plannedDate', 'actualDate']),
          this.formValidators.bothControlsRequiredValidator('startTime', 'endTime'),
        ],
      }
    );

    this.eventsForm
      .get('outlookEvent')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.eventsForm.get('outlookSubject').setValidators(Validators.required);
          this.eventsForm.get('startTime').setValidators(Validators.required);
          this.eventsForm.get('endTime').setValidators(Validators.required);
        } else {
          this.eventsForm.get('outlookSubject').clearValidators();
          this.eventsForm.get('startTime').clearValidators();
          this.eventsForm.get('endTime').clearValidators();
        }
      });
  }

  changeEventMode(mode: EventModalMode): void {
    this.mode = mode;
    this.cdr.detectChanges();
  }

  filterEventTypes(): void {
    if (this.eventOptions?.types.length > 0) {
      this.filteredEventTypes = [...this.eventOptions.types];

      if (!this.sharedService.isNullOrEmpty(this.typeSearchFilter)) {
        this.filteredEventTypes = this.eventOptions.types.filter((x) =>
          x.name?.toLowerCase().includes(this.typeSearchFilter.toLowerCase())
        );
      }
    }
  }

  // The search box matches anywhere in a person's name, or the start of their company, so typing "ALS"
  // narrows to ALS staff without "al" for a first name also listing every ALS member of staff.
  filterEventStaff(): void {
    this.filteredEventStaff = [...this.assigneeOptions];

    if (!this.sharedService.isNullOrEmpty(this.staffSearchFilter)) {
      const search = this.staffSearchFilter.toLowerCase();
      this.filteredEventStaff = this.assigneeOptions.filter(
        (x) => x.name?.toLowerCase().includes(search) || x.company?.toLowerCase().startsWith(search)
      );
    }

    this.assigneeGroups = this.groupAssignees(this.filteredEventStaff);
  }

  // Built when the dialog opens and again after each save, so "Currently assigned" follows the saved
  // assignee. The selection is then re-pointed at the matching entry, as the dropdown matches by object.
  private refreshAssigneeOptions(): void {
    this._staffSearchFilter = '';
    this.assigneeOptions = this.buildAssigneeOptions();
    this.filterEventStaff();

    const assignee = this.eventsForm?.controls.assignee;
    if (assignee?.value) {
      assignee.setValue(this.assigneeOptions.find((o) => o.id === assignee.value.id) ?? null, {
        emitEvent: false,
      });
    }
  }

  // Who can be assigned (#1807). The employer endpoint sends active staff from every organisation this
  // person can see, each with their company. Only when no assignee list is SENT (null — an options
  // endpoint that does not fill it, or an older API) does this fall back to the plain staff list; an empty list means nobody is offered.
  // When editing, the current assignee is always kept, even if they would no longer be listed (a leaver,
  // or someone outside the viewer's organisations), so opening an event never blanks the field.
  private buildAssigneeOptions(): EventAssigneeOption[] {
    const options: EventAssigneeOption[] =
      this.eventOptions.assignees != null
        ? [...this.eventOptions.assignees]
        : (this.eventOptions.staff ?? []).map((s) => ({ id: Number(s.id), name: s.name, company: null }));

    const current = this.event?.id != null ? this.event.assignee : null;
    if (current && !options.some((o) => o.id === current.id)) {
      options.unshift({ id: current.id, name: current.name, company: null, kept: true });
    }

    return options;
  }

  // One group per company: the kept current assignee first (they are the one selected), then the
  // business's own company, then the other companies alphabetically. Staff with no company (the fallback
  // list) form one group with no heading, so they are never labelled as "Currently assigned".
  private groupAssignees(assignees: EventAssigneeOption[]): EventAssigneeGroup[] {
    const byCompany = new Map<string | null, EventAssigneeOption[]>();
    for (const assignee of assignees.filter((a) => !a.kept)) {
      const company = assignee.company ?? null;
      if (!byCompany.has(company)) {
        byCompany.set(company, []);
      }
      byCompany.get(company).push(assignee);
    }

    const own = this.eventOptions.employerCompany ?? null;
    const rank = (company: string | null): number =>
      company === null
        ? AssigneeGroupRank.NoCompany
        : company === own
          ? AssigneeGroupRank.OwnCompany
          : AssigneeGroupRank.OtherCompany;

    const companyGroups: EventAssigneeGroup[] = [...byCompany.entries()]
      .sort(([a], [b]) => rank(a) - rank(b) || (a ?? '').localeCompare(b ?? ''))
      .map(([company, members]) => ({ company, label: company, assignees: members }));

    const kept = assignees.filter((a) => a.kept);
    return kept.length > 0
      ? [{ company: null, label: 'Currently assigned', assignees: kept }, ...companyGroups]
      : companyGroups;
  }

  // What a screen reader hears after a search, e.g. "12 assignees in 3 companies".
  private describeAssignees(): string {
    const people = this.filteredEventStaff.length;
    if (people === 0) {
      return 'No staff found.';
    }

    const companies = new Set(this.filteredEventStaff.filter((a) => a.company).map((a) => a.company)).size;
    const peopleText = `${people} ${people === 1 ? 'assignee' : 'assignees'}`;
    return companies > 0 ? `${peopleText} in ${companies} ${companies === 1 ? 'company' : 'companies'}` : peopleText;
  }

  filterEventBranch(): void {
    if (this.eventOptions?.branches.length > 0) {
      this.filteredEventBranches = [...this.eventOptions.branches];

      if (!this.sharedService.isNullOrEmpty(this.branchSearchFilter)) {
        this.filteredEventBranches = this.eventOptions.branches.filter((x) =>
          x.name?.toLowerCase().includes(this.branchSearchFilter.toLowerCase())
        );
      }
    }
  }

  onTypeChange(): void {
    if (!this.isPlannedEvent()) {
      this.eventsForm.controls.startTime.setValue(null);
      this.eventsForm.controls.endTime.setValue(null);
      this.eventsForm.controls.outlookEvent.setValue(false);
      this.eventsForm.controls.plannedDate.setValue(null);
    }
  }

  onOutlookEventChange(): void {
    if (!this.eventsForm.value.outlookEvent) {
      this.eventsForm.controls.startTime.setValue(null);
      this.eventsForm.controls.endTime.setValue(null);
    }
  }

  isPlannedEvent(): boolean {
    return this.plannedEventIds.includes(this.eventsForm.value.type?.id);
  }

  isHealthAndSafetyEvent(): boolean {
    return this.healthAndSafetyEventIds.includes(this.eventsForm.value.type?.id);
  }

  private getModifedEvent(): EmployerEvent {
    const formValues = this.eventsForm.getRawValue();

    const event: EmployerEvent = {
      id: this.event?.id,
      branchId: formValues.branch?.id !== undefined ? formValues.branch.id : null,
      branchName:
        formValues.branch?.id !== undefined
          ? this.eventOptions.branches.find((b) => b.id === formValues.branch.id)?.name
          : null,
      type: formValues.type,
      assignee: formValues.assignee ? { id: formValues.assignee.id, name: formValues.assignee.name } : null,
      notes: formValues.notes,
      plannedDate: formValues.plannedDate,
      actualDate: formValues.actualDate,
      startTime: formValues.startTime,
      endTime: formValues.endTime,
      isRoot: this.branchId === null || this.data.event?.isRoot,
      outlookEvent: { active: formValues.outlookEvent, id: this.event?.outlookEvent?.id },
      typeGroup: this.event?.typeGroup,
      editedBy: this.event?.editedBy,
      editedDate: this.event?.editedDate,
      outlookEventSubject: formValues.outlookSubject,
      outlookEventSuccessful: null,
    };

    return event;
  }

  onSubmit(): void {
    if (this.saving) {
      return;
    }

    this.saving = true;

    if (this.mode === EventModalMode.edit) {
      this.updateEvent();
      return;
    }

    this.createEvent();
  }

  createEvent(): void {
    const subscription = this.employerService
      .createEvent(this.rootId, this.branchId, this.getModifedEvent(), this.staffId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (createdEvent: EmployerEvent) => {
          this.event = createdEvent;
          this.refreshAssigneeOptions();
          let successMessage = 'Event Created.';
          if (createdEvent.outlookEventSuccessful !== null) {
            this.eventsForm.controls.outlookEvent.setValue(createdEvent.outlookEventSuccessful);
            successMessage = createdEvent.outlookEventSuccessful
              ? 'Event Created and added to Outlook.'
              : 'Event Created but failed to add to Outlook.';
          }
          this.matSnackBar.open(successMessage, '', {
            duration: 2000,
          });
        },
        error: (error) => {
          const errorMessage =
            error === 'You do not have permission to complete the requested action.'
              ? 'You do not have permission to create an event.'
              : this.saveError;
          this.matSnackBar.open(errorMessage, '', {
            duration: 4000,
          });
          console.error('Error creating event', error);
          this.saving = false;
        },
        complete: () => {
          subscription.unsubscribe();
          this.eventsForm.markAsPristine();
          this.refreshEvents = true;
          this.saving = false;
          this.changeEventMode(EventModalMode.view);
        },
      });
  }

  updateEvent(): void {
    const subscription = this.employerService
      .updateEvent(this.getModifedEvent(), this.staffId, this.rootId, this.branchId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedEvent: EmployerEvent) => {
          this.event = updatedEvent;
          this.refreshAssigneeOptions();
          let successMessage = 'Event Created.';
          if (updatedEvent.outlookEventSuccessful !== null) {
            this.eventsForm.controls.outlookEvent.setValue(updatedEvent.outlookEventSuccessful);
            successMessage = updatedEvent.outlookEventSuccessful
              ? 'Event Updated and added to Outlook.'
              : 'Event Updated but failed to add to Outlook.';
          }
          this.matSnackBar.open(successMessage, '', {
            duration: 2000,
          });
        },
        error: (error) => {
          const errorMessage =
            error === 'You do not have permission to complete the requested action.'
              ? 'You do not have permission to edit this event.'
              : this.saveError;
          this.matSnackBar.open(errorMessage, '', {
            duration: 4000,
          });
          console.error('Error updating event', error);
          this.saving = false;
        },
        complete: () => {
          subscription.unsubscribe();
          this.eventsForm.markAsPristine();
          this.refreshEvents = true;
          this.saving = false;
          this.changeEventMode(EventModalMode.view);
        },
      });
  }

  close(): void {
    if (!this.eventsForm.dirty) {
      this.dialogRef.close({ refresh: this.refreshEvents });
      return;
    }

    const confirmDialogConfig = new MatDialogConfig();
    confirmDialogConfig.data = {
      message: `You are about to close without saving, **any changes made to this event will be lost**. Do you wish to continue?`,
      title: 'Are you sure?',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      type: 'warn',
    };

    this.matDialog
      .open(ConfirmDialogComponent, confirmDialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.dialogRef.close({ refresh: this.refreshEvents });
        }
      });
  }

  delete(): void {
    if (!this.permissions.canDelete || this.saving) {
      return;
    }

    const confirmDialogConfig = new MatDialogConfig();
    confirmDialogConfig.data = {
      message: `You are about to **permanently delete ${this.event.type.name} event**. Do you wish to continue?`,
      title: 'Are you sure?',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      type: 'warn',
    };

    this.matDialog
      .open(ConfirmDialogComponent, confirmDialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.saving = true;

          const subscription = this.employerService
            .deleteEvent(this.event.id, this.staffId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.matSnackBar.open('Event Deleted.', '', {
                  duration: 2000,
                });
              },
              error: (error) => {
                const errorMessage =
                  error === 'You do not have permission to complete the requested action.'
                    ? 'You do not have permission to delete this event.'
                    : 'Error deleting event. Please try again.';
                this.matSnackBar.open(errorMessage, '', {
                  duration: 4000,
                });
                console.error('Error deleting event', error);
                this.saving = false;
              },
              complete: () => {
                subscription.unsubscribe();
                this.refreshEvents = true;
                this.saving = false;
                this.dialogRef.close({ refresh: this.refreshEvents });
              },
            });
        }
      });
  }

  showBranchOnView(): boolean {
    // H&S event, should only be on root and needs to have a branch name as this is used for location
    if (this.isHealthAndSafetyEvent && this.event.isRoot && this.event.branchName !== null) {
      return true;
    } // If we are on root (so no branch id) and this event is not a root event, we display what branch this event is from
    else if (this.branchId === null && !this.event.isRoot) {
      return true;
    } // this acts as a override to show the branch name on the view if there is one
    else if (this.data.showBranch === true && this.event.branchName !== null) {
      return true;
    }

    return false;
  }
}
